import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "strengthMeter", "container", "requirementsList", "minLength"]
  static values = {
    minLength: { type: Number, default: 8 },
    requireUppercase: { type: Boolean, default: true },
    requireLowercase: { type: Boolean, default: true },
    requireNumber: { type: Boolean, default: true },
    requireSpecialChar: { type: Boolean, default: true },
  }

  connect() {
    if (this.hasMinLengthTarget) {
      this.minLengthTarget.textContent = this.minLengthValue
    }

    this.boundUpdate = this.updateStrengthMeter.bind(this)
    this.inputTarget.addEventListener("input", this.boundUpdate)

    if (this.hasStrengthMeterTarget) {
      this.strengthMeterTarget.innerHTML = ""
      for (let i = 0; i < 5; i++) {
        const bar = document.createElement("div")
        bar.classList.add(
          "strength-bar",
          "h-2",
          "flex-1",
          "rounded",
          "bg-gray-200",
          "transition-colors",
          "duration-300",
        )
        this.strengthMeterTarget.appendChild(bar)
      }
    }

    this.updateStrengthMeter()
  }

  disconnect() {
    this.inputTarget.removeEventListener("input", this.boundUpdate)
  }

  updateStrengthMeter() {
    const password = this.inputTarget.value

    if (this.hasContainerTarget) {
      this.containerTarget.classList.toggle("hidden", password.length === 0)
    }

    if (password.length === 0) return

    const requirements = this.checkRequirements(password)
    const strength = this.calculateStrength(requirements, password)

    this.updateStrengthBars(strength)
    this.updateRequirementsList(requirements)
  }

  checkRequirements(password) {
    return {
      length: password.length >= this.minLengthValue,
      uppercase: this.requireUppercaseValue ? /[A-Z]/.test(password) : true,
      lowercase: this.requireLowercaseValue ? /[a-z]/.test(password) : true,
      number: this.requireNumberValue ? /[0-9]/.test(password) : true,
      special: this.requireSpecialCharValue ? /[^A-Za-z0-9]/.test(password) : true,
    }
  }

  calculateStrength(requirements, password) {
    let strength = 0

    strength += requirements.length ? 1 : 0
    strength += requirements.uppercase ? 1 : 0
    strength += requirements.lowercase ? 1 : 0
    strength += requirements.number ? 1 : 0
    strength += requirements.special ? 1 : 0

    if (requirements.length) {
      strength += Math.min(Math.floor((password.length - this.minLengthValue) / 2), 1)
    }

    return Math.min(strength, 5)
  }

  updateStrengthBars(strength) {
    if (!this.hasStrengthMeterTarget) return

    const bars = this.strengthMeterTarget.querySelectorAll(".strength-bar")

    bars.forEach((bar, index) => {
      bar.className = "strength-bar h-2 flex-1 rounded transition-colors duration-300"

      if (index < strength) {
        if (strength <= 2) {
          bar.classList.add("bg-red-500")
        } else if (strength === 3) {
          bar.classList.add("bg-yellow-400")
        } else if (strength === 4) {
          bar.classList.add("bg-green-300")
        } else {
          bar.classList.add("bg-green-500")
        }
      } else {
        bar.classList.add("bg-gray-300")
      }
    })
  }

  updateRequirementsList(requirements) {
    if (!this.hasRequirementsListTarget) return

    const requirementItems = {
      length: this.requirementsListTarget.querySelector('[data-requirement="length"]'),
      number: this.requirementsListTarget.querySelector('[data-requirement="number"]'),
      special: this.requirementsListTarget.querySelector('[data-requirement="special"]'),
      uppercase: this.requirementsListTarget.querySelector('[data-requirement="uppercase"]'),
      lowercase: this.requirementsListTarget.querySelector('[data-requirement="lowercase"]'),
    }

    Object.entries(requirements).forEach(([key, met]) => {
      const item = requirementItems[key]
      if (!item) return

      const uncheckedIcon = item.querySelector('[data-icon="unchecked"]')
      const checkedIcon = item.querySelector('[data-icon="checked"]')

      if (met) {
        item.classList.add("text-green-600")
        item.classList.remove("text-gray-500", "text-red-600")
        uncheckedIcon?.classList.add("hidden")
        checkedIcon?.classList.remove("hidden")
      } else {
        item.classList.add("text-red-600")
        item.classList.remove("text-gray-500", "text-green-600")
        uncheckedIcon?.classList.remove("hidden")
        checkedIcon?.classList.add("hidden")
      }
    })
  }
}
